import React, { useEffect, useRef, useState, KeyboardEvent } from 'react';
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI';

export interface EmployeeSuggestion {
    AccountEmployeeId: number;
    FullName: string;
    EmailAddress: string;
    IsEmailVerified: boolean;
}

export interface EmailTagInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    onVerifiedChange?: (email: string, isVerified: boolean, employeeId?: number) => void;
    onBlur?: () => void;
    placeholder?: string;
    hasError?: boolean;
    maxTags?: number;
    excludeEmails?: string[];
    verifiedMap?: Record<string, boolean>;
}

const EmailTagInput: React.FC<EmailTagInputProps> = ({ value, onChange, onVerifiedChange, onBlur, placeholder = 'Type to search employee...', hasError = false, maxTags = 10, excludeEmails = [], verifiedMap: externalVerifiedMap }) => {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');
    const [internalVerifiedMap, setInternalVerifiedMap] = useState<Record<string, boolean>>({});
    const [suggestions, setSuggestions] = useState<EmployeeSuggestion[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const verifiedMap = externalVerifiedMap ?? internalVerifiedMap;

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
                setShowDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const searchByEmail = async (query: string) => {
        if (!query.trim()) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        setLoading(true);
        try {
            const response: any = await fetchTimeliveAPI(`api/Employee/GetCurrentActiveEmployeesByEmail/${query.trim()}`, 'GET', '', true);
            const json = await response.json();
            if (json.StatusCode === '200' && json.Data?.length > 0) {
                const allExcluded = [...value, ...excludeEmails].map(e => e.toLowerCase());
                const filtered = json.Data.filter((emp: EmployeeSuggestion) =>
                    !allExcluded.includes(emp.EmailAddress.toLowerCase())
                );
                setSuggestions(filtered);
                setShowDropdown(filtered.length > 0);
                setInputError('');
            } else {
                setSuggestions([]);
                setShowDropdown(false);
                setInputError('Email address does not exist'); 
            }
        } catch {
            setSuggestions([]);
            setShowDropdown(false);
        } finally {
            setLoading(false);
        }
    };

    const addTagFromSuggestion = (emp: EmployeeSuggestion) => {
        const allExcluded = [...value, ...excludeEmails].map(e => e.toLowerCase());
        if (allExcluded.includes(emp.EmailAddress.toLowerCase())) {
            setInputError('This email is already used');
            return;
        }
        if (value.length >= maxTags) {
            setInputError(`Maximum ${maxTags} emails allowed`);
            return;
        }
        setInputError('');
        onChange([...value, emp.EmailAddress]);
        setInputValue('');
        if (!externalVerifiedMap) {
            setInternalVerifiedMap(prev => ({ ...prev, [emp.EmailAddress.toLowerCase()]: emp.IsEmailVerified }));
        }
        onVerifiedChange?.(emp.EmailAddress, emp.IsEmailVerified, emp.AccountEmployeeId);
        setSuggestions([]);
        setShowDropdown(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
    };

    const removeTag = (index: number) => {
        setInputError(''); 
        const removed = value[index];
        if (!externalVerifiedMap) {
            setInternalVerifiedMap(prev => {
                const next = { ...prev };
                delete next[removed.toLowerCase()];
                return next;
            });
        }
        onVerifiedChange?.(removed, true, undefined);
        onChange(value.filter((_, i) => i !== index));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        setInputError('');
        setActiveIndex(-1);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchByEmail(val), 300);
    };

      const validateAndAddEmail = async (email: string) => {
        const emailLower = email.trim().toLowerCase();
        const allExcluded = [...value, ...excludeEmails].map(e => e.toLowerCase());
        
        if (allExcluded.includes(emailLower)) {
            setInputError('This email is already added');
            return;
        }

        setLoading(true);
        try {
            const response: any = await fetchTimeliveAPI(`api/Employee/GetCurrentActiveEmployeesByEmail/${email.trim()}`, 'GET', '', true);
            const json = await response.json();
            const emp = json.Data?.find((e: any) => e.EmailAddress.toLowerCase() === emailLower);
            
            if (json.StatusCode === '200' && emp) {
                addTagFromSuggestion(emp);
                setInputError('');
            } else {
                setInputError('Email address does not exist');
            }
        } catch {
            setInputError('Error validating email');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (showDropdown && suggestions.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, suggestions.length - 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
            if (e.key === 'Enter') {
                e.preventDefault();
                addTagFromSuggestion(suggestions[activeIndex >= 0 ? activeIndex : 0]);
                return;
            }
            if (e.key === 'Escape') { setShowDropdown(false); return; }
        }
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            const emailLower = inputValue.trim().toLowerCase();
            validateAndAddEmail(inputValue.trim());
            // const allExcluded = excludeEmails.map(ex => ex.toLowerCase());
            // if (allExcluded.includes(emailLower)) {
            //     setInputError('This email is already added');
            //     return;
            // }
        }
        if (e.key === 'Backspace' && !inputValue && value.length > 0) removeTag(value.length - 1);
    };

    const handleBlur = () => {
        setTimeout(() => {
            setShowDropdown(false);
            if (inputValue.trim()) {
                const emailLower = inputValue.trim().toLowerCase();
                const allExcluded = [...excludeEmails, ...value].map(ex => ex.toLowerCase());
                if (allExcluded.includes(emailLower)) {
                    setInputError('This email is already added');
                }
            }
            onBlur?.();
        }, 150);
    };

    const borderColor = hasError || inputError ? '#dc3545' : '#ced4da';
    const shadow = hasError || inputError ? '0 0 0 0.2rem rgba(220,53,69,.15)' : undefined;

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <div
                onClick={() => inputRef.current?.focus()}
                style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px',
                    padding: '5px 8px', border: `1px solid ${borderColor}`, borderRadius: '4px',
                    cursor: 'text', minHeight: '38px', backgroundColor: '#fff',
                    boxShadow: shadow, transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
            >
                {value.map((email, idx) => {
                    const isVerified = verifiedMap[email.toLowerCase()] ?? true;
                    return (
                        <span key={idx} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            background: '#e8f0fe', border: '1px solid #c5d8fc', borderRadius: '4px',
                            padding: '2px 6px', fontSize: '12px', color: '#1a56db',
                            fontWeight: 500, whiteSpace: 'nowrap',
                        }}>
                            {email}
                            <span
                                title={isVerified ? 'Email Verified' : 'Email Not Verified'}
                                style={{
                                    fontSize: '10px', fontWeight: 600,
                                    background: isVerified ? '#40af73' : '#ffa700',
                                    color: isVerified ? '#fff' : '#212529',
                                    borderRadius: '3px', padding: '1px 4px', marginLeft: '2px',
                                }}
                            >
                                {isVerified ? 'Verified' : 'Unverified'}
                            </span>
                            <span
                                onClick={(e) => { e.stopPropagation(); removeTag(idx); }}
                                style={{ cursor: 'pointer', fontSize: '14px', lineHeight: 1, color: '#6c7eab', fontWeight: 700, marginLeft: '2px' }}
                                title="Remove"
                            >×</span>
                        </span>
                    );
                })}
                <input
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                    placeholder={value.length === 0 ? placeholder : ''}
                    style={{
                        border: 'none', outline: 'none', flex: '1 1 140px',
                        minWidth: '140px', fontSize: '13px', padding: '2px 0', background: 'transparent',
                    }}
                />
                {loading && (
                    <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '2px' }}>
                        <i className="fas fa-circle-notch fa-spin" />
                    </span>
                )}
            </div>

            {showDropdown && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                    background: '#fff', border: '1px solid #d0d7de', borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: '200px',
                    overflowY: 'auto', marginTop: '2px',
                }}>
                    {suggestions.map((emp, idx) => (
                        <div
                            key={emp.AccountEmployeeId}
                            onMouseDown={() => addTagFromSuggestion(emp)}
                            onMouseEnter={() => setActiveIndex(idx)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '8px 12px', cursor: 'pointer',
                                background: activeIndex === idx ? '#f0f4ff' : '#fff',
                                borderBottom: idx < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                transition: 'background 0.1s',
                            }}
                        >
                            <div style={{
                                width: '30px', height: '30px', borderRadius: '50%',
                                background: '#0C66E4', color: '#fff', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', fontWeight: 700,
                            }}>
                                {emp.FullName.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {emp.FullName}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {emp.EmailAddress}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {inputError && <div className="text-danger fs-8 mt-1">{inputError}</div>}
        </div>
    );
};

export default EmailTagInput;